<template>
  <div>
    <template v-if="!loading.component">
      <Pagination
        :page="page"
        :per-page="pageSize"
        :options="perPageOptions"
        :total-count="totalCount"
        @change="
          handlePageChange({ page: $event.page, pageSize: $event.perPage })
        "
      />
      <v-data-table
        class="table"
        :headers="headers"
        :items="items"
        :page.sync="page"
        :items-per-page="pageSize"
        item-key="key"
        hide-default-footer
      >
        <template #item.name="{ item }">
          <FormDatagridRow
            :item="item"
            @export-stats="exportStatsHandler"
            @export-links="exportLinks"
          />
        </template>
      </v-data-table>
      <Pagination
        :page="page"
        :per-page="pageSize"
        :options="perPageOptions"
        :total-count="totalCount"
        :show-options="false"
        @change="
          handlePageChange({ page: $event.page, pageSize: $event.perPage })
        "
      />
    </template>
    <AsyncDownloadDialog
      v-if="showAsyncDownloadDialog"
      :export-id="exportId"
      @close="showAsyncDownloadDialog = false"
    />
    <CalendarDialog
      v-if="showCalendarDialog"
      @close="showCalendarDialog = false"
      @change="dateRangeChange"
    />
    <SectionLoader :status="loading.component" />
  </div>
</template>

<script>
import FormDatagridRow from "@/components/ReviewBooster/FormDatagridRow";
import Pagination from "@/components/List/Pagination";
import FileExporter from "@/utils/file-exporter";
import reviewBoosterService from "@/services/review-booster";
import serviceHandler from "@/helpers/handlers/service";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "ReviewBoosterFormDatagrid",
  components: {
    Pagination,
    FormDatagridRow,
    CalendarDialog: () => import("@/components/ReviewBooster/CalendarDialog"),
    AsyncDownloadDialog: () => import("@/components/AsyncDownloadDialog"),
    SectionLoader: () => import("@/components/Loader/SectionLoader")
  },
  props: {},
  data() {
    return {
      //TODO paging when ready
      page: 1,
      pageSize: 10,
      totalCount: 0,
      items: [],
      perPageOptions: [10, 25, 50, 100],
      exportDateRange: null,
      loading: {
        component: false,
        export: false
      },
      exportId: null,
      formKey: null,
      showAsyncDownloadDialog: false,
      showCalendarDialog: false
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("message.common.name"),
          align: "start",
          sortable: false,
          value: "name"
        }
      ];
    }
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    exportStatsHandler(key, file_format = "csv") {
      this.formKey = key;
      this.toggleCalendarDialog(file_format);
    },

    toggleCalendarDialog() {
      this.showCalendarDialog = !this.showCalendarDialog;
    },

    async exportLinks({ key, file_format = "csv" }) {
      await new FileExporter().exportFile(() =>
        reviewBoosterService.exportReviewBoosterLinks(key, file_format)
      );
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.RB_FORM_LIST_LINKS_EXPORT,
        format: file_format
      });
    },

    async dateRangeChange({ dateRange, file_format = "csv" }) {
      const [dateFrom, dateTo] = dateRange;
      const filters = { date_from: dateFrom, date_to: dateTo };
      await this.exportStats(filters, file_format);
      this.toggleCalendarDialog();
      this.showAsyncDownloadDialog = true;
    },

    async exportStats(payload, file_format = "csv") {
      try {
        this.loading.export = true;

        const { export_id } = await serviceHandler(
          reviewBoosterService.exportReviewBoosterStats(
            this.formKey,
            payload,
            file_format
          )
        );
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.RB_FORM_LIST_STATS_EXPORT,
          format: file_format,
          date_from: payload.date_from,
          date_to: payload.date_to
        });
        this.loading.export = false;

        this.exportId = export_id;
        this.showAsyncDownloadDialog = true;
        this.key = null;
      } catch (err) {
        this.loading.export = false;
      }
    },

    async fetchData() {
      this.loading.component = true;
      const { results, count } = await serviceHandler(
        reviewBoosterService.getReviewBoosterList({
          page: this.page,
          page_size: this.pageSize
        })
      );

      this.items = results;
      this.totalCount = count;
      this.loading.component = false;
    },

    handlePageChange({ page, pageSize }) {
      this.page = page;
      this.pageSize = pageSize;
      this.fetchData();
    }
  }
};
</script>

<style lang="scss">
.table.theme--light.v-data-table {
  color: $text;
  @include font-size(14px);
  line-height: 17px;

  thead tr th {
    font-weight: normal;
    @include font-size(12px);
    line-height: 15px;
    border-bottom: none !important;
  }

  thead tr:last-child th {
    vertical-align: bottom;
  }

  td {
    height: 80px;
    padding: 0;
  }
}
</style>
